import type { CommandOutcome, ConnectionPhase, DeviceState, HeadId, Notice, SettingDescriptor } from "./model";

export type CommandPhase = "sending" | "applied" | "queued" | "saving" | "saved" | "verifying" | "error";

export interface CommandStatus {
  action: string;
  phase: CommandPhase;
  requestId?: number;
}

export interface RemoteState {
  connection: ConnectionPhase;
  device?: DeviceState;
  settingsDraft: SettingDescriptor[];
  busyAction?: string;
  commandStatus?: CommandStatus;
  lastDeviceId?: string;
  connectionNoticeId?: number;
  notices: Notice[];
}

export type RemoteAction =
  | { type: "CONNECTION"; phase: ConnectionPhase; lastDeviceId?: string }
  | { type: "SYNCED"; device: DeviceState; lastDeviceId?: string }
  | { type: "LIVE_HEAD"; head: DeviceState["heads"][HeadId] }
  | { type: "DRAFT_SETTING"; id: number; value: number | boolean }
  | { type: "RESET_DRAFT" }
  | { type: "BUSY"; action?: string }
  | { type: "COMMAND_START"; action: string }
  | { type: "COMMAND_UNCERTAIN"; action: string }
  | { type: "COMMAND_RECOVERY_FAILED"; action: string }
  | { type: "COMMAND_COMPLETION_UNCERTAIN"; requestId: number }
  | { type: "COMMAND_COMPLETION_RECOVERY_FAILED"; requestId: number }
  | { type: "COMMAND_RESULT"; action: string; outcome: CommandOutcome }
  | { type: "COMMAND_COMPLETED"; requestId: number; outcome: CommandOutcome }
  | { type: "COMMAND_SETTLED"; persistencePending: boolean; persistenceFailed: boolean }
  | { type: "COMMAND_CLEAR"; requestId?: number }
  | { type: "NOTICE"; notice: Notice }
  | { type: "DISMISS_NOTICE"; id: number }
  | { type: "DISCONNECTED"; error?: string };

export const initialRemoteState: RemoteState = {
  connection: "disconnected",
  settingsDraft: [],
  notices: [],
};

export function commandBlocksMutation(status?: CommandStatus): boolean {
  return Boolean(status && ["sending", "queued", "saving", "verifying"].includes(status.phase));
}

function sameSettingValues(left: SettingDescriptor[], right: SettingDescriptor[]): boolean {
  return left.length === right.length && left.every((setting, index) => {
    const candidate = right[index];
    return candidate?.id === setting.id && candidate.value === setting.value;
  });
}

function commandPhase(outcome: CommandOutcome, completed = false): CommandPhase | undefined {
  switch (outcome.code) {
    case "ok": return completed ? "saved" : "applied";
    case "queued": return "queued";
    case "persistence_pending": return "saving";
    case "confirmation_required": return undefined;
    default: return "error";
  }
}

export function remoteReducer(state: RemoteState, action: RemoteAction): RemoteState {
  switch (action.type) {
    case "CONNECTION":
      return { ...state, connection: action.phase, lastDeviceId: action.lastDeviceId ?? state.lastDeviceId };
    case "SYNCED": {
      const authoritativeSettingsChanged =
        !state.device || !sameSettingValues(state.device.settings, action.device.settings);
      const preserveDirtyDraft =
        !authoritativeSettingsChanged && settingsChanged(state);
      const sessionChanged = Boolean(
        state.device && state.device.sessionId !== action.device.sessionId,
      );
      return {
        ...state,
        connection: "connected",
        device: action.device,
        settingsDraft: preserveDirtyDraft
          ? state.settingsDraft
          : action.device.settings.map((setting) => ({ ...setting })),
        lastDeviceId: action.lastDeviceId ?? state.lastDeviceId,
        notices: state.connectionNoticeId === undefined
          ? state.notices
          : state.notices.filter((notice) => notice.id !== state.connectionNoticeId),
        connectionNoticeId: undefined,
        commandStatus: sessionChanged ? undefined : state.commandStatus,
      };
    }
    case "LIVE_HEAD":
      if (!state.device) return state;
      return {
        ...state,
        device: {
          ...state.device,
          heads: { ...state.device.heads, [action.head.id]: action.head },
        },
      };
    case "DRAFT_SETTING":
      return {
        ...state,
        settingsDraft: state.settingsDraft.map((setting) =>
          setting.id === action.id ? { ...setting, value: action.value } : setting,
        ),
      };
    case "RESET_DRAFT":
      return {
        ...state,
        settingsDraft: state.settingsDraft.map((setting) => ({ ...setting, value: setting.defaultValue })),
      };
    case "BUSY":
      return { ...state, busyAction: action.action };
    case "COMMAND_START":
      return { ...state, commandStatus: { action: action.action, phase: "sending" } };
    case "COMMAND_UNCERTAIN":
      return { ...state, commandStatus: { action: action.action, phase: "verifying" } };
    case "COMMAND_RECOVERY_FAILED":
      return { ...state, commandStatus: { action: action.action, phase: "error" } };
    case "COMMAND_COMPLETION_UNCERTAIN":
      if (state.commandStatus?.requestId !== action.requestId) return state;
      return {
        ...state,
        commandStatus: { ...state.commandStatus, phase: "verifying" },
      };
    case "COMMAND_COMPLETION_RECOVERY_FAILED":
      if (state.commandStatus?.requestId !== action.requestId) return state;
      return {
        ...state,
        commandStatus: { ...state.commandStatus, phase: "error" },
      };
    case "COMMAND_RESULT": {
      const phase = commandPhase(action.outcome);
      return {
        ...state,
        commandStatus: phase
          ? { action: action.action, phase, requestId: action.outcome.requestId }
          : undefined,
      };
    }
    case "COMMAND_COMPLETED": {
      if (state.commandStatus?.requestId !== action.requestId) return state;
      const phase = commandPhase(action.outcome, true);
      return {
        ...state,
        commandStatus: phase
          ? { ...state.commandStatus, phase }
          : undefined,
      };
    }
    case "COMMAND_SETTLED": {
      if (state.commandStatus?.phase !== "saving") return state;
      if (action.persistenceFailed) {
        return { ...state, commandStatus: { ...state.commandStatus, phase: "error" } };
      }
      if (action.persistencePending) return state;
      return { ...state, commandStatus: { ...state.commandStatus, phase: "saved" } };
    }
    case "COMMAND_CLEAR":
      if (action.requestId !== undefined && state.commandStatus?.requestId !== action.requestId) return state;
      return { ...state, commandStatus: undefined };
    case "NOTICE":
      return { ...state, notices: [...state.notices.slice(-2), action.notice] };
    case "DISMISS_NOTICE":
      return { ...state, notices: state.notices.filter((notice) => notice.id !== action.id) };
    case "DISCONNECTED": {
      const noticeId = Date.now();
      const notices = state.connectionNoticeId === undefined
        ? state.notices
        : state.notices.filter((notice) => notice.id !== state.connectionNoticeId);
      return {
        ...state,
        connection: action.error ? "error" : "disconnected",
        busyAction: undefined,
        commandStatus: undefined,
        connectionNoticeId: action.error ? noticeId : undefined,
        notices: action.error
          ? [...notices.slice(-2), { id: noticeId, tone: "error", text: action.error }]
          : notices,
      };
    }
  }
}

export function settingsChanged(state: RemoteState): boolean {
  if (!state.device || state.settingsDraft.length !== state.device.settings.length) return false;
  return state.settingsDraft.some((draft) => {
    const original = state.device?.settings.find((setting) => setting.id === draft.id);
    return !original || original.value !== draft.value;
  });
}

export function validateSettings(settings: SettingDescriptor[]): Map<number, string> {
  const errors = new Map<number, string>();
  for (const setting of settings) {
    if (setting.kind !== "number") continue;
    const value = Number(setting.value);
    if (!Number.isFinite(value)) errors.set(setting.id, "Enter a number");
    else if (setting.min !== undefined && value < setting.min) errors.set(setting.id, `Minimum ${setting.min}`);
    else if (setting.max !== undefined && value > setting.max) errors.set(setting.id, `Maximum ${setting.max}`);
    else if (setting.step !== undefined && setting.step > 0) {
      const base = setting.min ?? 0;
      const stepOffset = (value - base) / setting.step;
      if (Math.abs(stepOffset - Math.round(stepOffset)) > 1e-4) {
        errors.set(setting.id, `Use increments of ${setting.step}`);
      }
    }
  }
  const compare = (minimumKey: string, maximumKey: string, label: string): void => {
    const minimum = settings.find((setting) => setting.key === minimumKey);
    const maximum = settings.find((setting) => setting.key === maximumKey);
    if (minimum && maximum && Number(minimum.value) > Number(maximum.value)) {
      errors.set(maximum.id, `${label} maximum must be at least its minimum`);
    }
  };
  compare("prewetMinimumMl", "prewetMaximumMl", "Prewet volume");
  compare("mainMinimumMl", "mainMaximumMl", "Main volume");
  compare("mainMinimumSeconds", "mainMaximumSeconds", "Main time");
  return errors;
}
